import { httpService } from "./http.service";

const BASE_URL = "boards";

export const boardService = {
    query,
    getById,
    getByIds,
    getByIdRequest,
    getFilteredBoard,
    addBoard,
    addFolder,
    save,
    remove,
    removeGroup,
    removeTask,
    addAutomationBoard,
    getAutomationByBoardId,
    deleteAutomationBoard,
    setAutomationGroupPause,
    getDefaultFilterBoard,
    getDefaultFilterBoards,
    getFilterFromSearchParams,
    getEmptyGroup,
    getEmptyTask,
    getEmptyComment,
    getEmptyActivity,
    getEmptyBoard,
    updateTask,
    updateGroup,
    addComment,
    deleteComment,
    updateComment,
    updateLabel,
    updateOrder,
    updateAndSyncTitle,
    deleteOrder,
    addActivity,
    deactivateTasks,
    getFormPage,
    checkPageFormExists,
    createBoardFromBpmn,
    choicePreRegistration
};

async function createBoardFromBpmn(accountId, payload) {
    try {
        const data = await httpService.post(
            `${BASE_URL}/account/${accountId}/boards/from-bpmn`,
            {
                desktopId: payload.desktopId,
                title: payload.title,
                description: payload.description,
                createdBy: payload.createdBy,
                members: payload.members || [],
                bpmnXml: payload.bpmnXml,
                groupColors: payload.groupColors,
                groupColorsByTitle: payload.groupColorsByTitle,
                groupColor: payload.groupColor,
            }
        );
        return data;
    } catch (error) {
        console.error("Failed to create board from BPMN:", error);
        throw error;
    }
}

async function query(filter = getDefaultFilterBoards()) {
    try {
        const data = await httpService.get(`${BASE_URL}/boards`);

        const filteredBoards = data.filter(board => {
            return board.title.includes(filter.title) && board.isStarred === filter.isStarred;
        });
        return filteredBoards;
    } catch (error) {
        console.error('Failed to fetch boards:', error);
        throw error;
    }
}

async function addBoard(obj, accountId) {
    try {
        const data = await httpService.post(`${BASE_URL}/account/${accountId}/boards`, obj);
        return data;
    } catch (error) {
        console.error('Failed to fetch board:', error);
        throw error;
    }
}

async function addAutomationBoard(boardId, accountId, payload) {
    try {
        const data = await httpService.post(`${BASE_URL}/account/${accountId}/board/${boardId}/addAutomation`, payload);
        return data;
    } catch (error) {
        console.error('Failed to fetch Automation:', error);
        throw error;
    }
}

async function getAutomationByBoardId(boardId, accountId) {
    try {
        const data = await httpService.get(`${BASE_URL}/account/${accountId}/board/${boardId}/getAutomation`);
        return data;
    } catch (error) {
        console.error('Failed to fetch Automation:', error);
        throw error;
    }
}

async function deleteAutomationBoard(boardId, accountId, automationId, userId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/account/${accountId}/board/${boardId}/deleteAutomation/${automationId}/${userId}`);
        return data;
    } catch (error) {
        console.error('Failed to delete Automation:', error);
        throw error;
    }
}

async function setAutomationGroupPause(boardId, accountId, automationId, payload) {
    try {
        const { data } = await httpService.patch(
            `${BASE_URL}/account/${accountId}/board/${boardId}/automation/${automationId}/group-pause`,
            payload
        );
        return data;
    } catch (error) {
        console.error('Failed to update automation group pause:', error);
        throw error;
    }
}

async function addFolder(obj, accountId) {
    try {
        const data = await httpService.post(`${BASE_URL}/accountId/${accountId}/folders`, obj);
        return data;
    } catch (error) {
        console.error('Failed to fetch folder:', error);
        throw error;
    }
}

async function updateAndSyncTitle(obj, accountId) {
    try {
        const data = await httpService.patch(`${BASE_URL}/account/${accountId}/tree/sync-title`, obj);
        return data;
    } catch (error) {
        console.error('Failed to fetch folder:', error);
        throw error;
    }
}

function getFilteredBoard(board, filterBy = getDefaultFilterBoard(), groups, tasks) {
    const { title, memberId } = filterBy;

    if (!title && !memberId) {
        return {
            ...board,
            groupsBySearch: [...groups],
            groupsByMembers: [...groups]
        };
    }

    const lowerTitle = title ? title.toLowerCase() : null;
    const statusColumns = board.cmpsOrder?.filter(column => column.type === "status-picker") || [];

    const isMatch = (value) => {
        if (!lowerTitle) return false;

        if (typeof value === "string") {
            return value.toLowerCase().includes(lowerTitle);
        }
        if (typeof value === "number") {
            return value.toString().includes(lowerTitle);
        }
        if (Array.isArray(value)) {
            return value.some(item => isMatch(item));
        }
        if (typeof value === "object" && value !== null) {
            return Object.entries(value).some(([key, val]) => key !== 'comments' && isMatch(val));
        }
        return false;
    };

    const filteredGroupsBySearch = groups?.map((group) => {
        // Traz as tarefas reais do Redux
        const groupTaskObjects = tasks.filter(task =>
            group.tasks.includes(task._id) && task.active !== false
        );

        const matchingTasks = groupTaskObjects.filter((task) => {
            const taskMatchesTitle = isMatch(task.title);

            const taskMatchesAnyStatus = statusColumns.some(statusColumn =>
                task.dynamicFields?.some(field => {
                    if (field.type === "status-picker") {
                        const statusTitle = statusColumn.labels?.find(label => label.id === field.value)?.title?.toLowerCase();
                        return statusTitle && statusTitle.includes(lowerTitle);
                    }
                    return false;
                })
            );

            const taskMatchesComments = task.comments?.some(comment =>
                comment.txt?.toLowerCase().includes(lowerTitle)
            );

            const taskMatchesDynamic = isMatch(task.dynamicFields);

            return taskMatchesTitle || taskMatchesAnyStatus || taskMatchesComments || taskMatchesDynamic;
        });

        return {
            ...group,
            tasks: matchingTasks.map(task => task._id)
        };
    });

    return {
        ...board,
        groupsBySearch: filteredGroupsBySearch.length > 0 ? filteredGroupsBySearch : [...groups],
        groupsByMembers: [...groups],
    };
}



async function getById(boardId) {
    try {
        const data = await httpService.get(`${BASE_URL}/boards/${boardId}`);
        return data;
    } catch (error) {
        console.error('Failed to fetch board:', error);
        throw error;
    }
}

async function getByIds(boardsId, userId) {
    try {
        const data = await httpService.post(`${BASE_URL}/boards/bulk`, {
            boardIds: boardsId,
            userId,
        });
        return data?.boards;
    } catch (error) {
        console.error('Failed to fetch boards:', error);
        throw error;
    }
}


async function checkPageFormExists(pageName, boardId) {
    try {
        const data = await httpService.post(`${BASE_URL}/check-page-name`, { pageName, boardId });
        return data.exists;
    } catch (error) {
        console.error('Erro ao verificar duplicidade do pageName:', error);
        throw error;
    }
}


async function getByIdRequest(boardId) {
    try {
        const data = await httpService.get(`${BASE_URL}/boardsRequest/${boardId}`);
        return data;
    } catch (error) {
        console.error('Failed to fetch board:', error);
        throw error;
    }
}

async function removeGroup(boardId, groupId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/boards/${boardId}/groups/${groupId}`);
        return data
    } catch (error) {
        console.error('Failed to remove Group', error);
        throw error
    }
}

async function removeTask(boardId, groupId, taskId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/boards/${boardId}/groups/${groupId}/tasks/${taskId}`);
        return data
    } catch (error) {
        console.error('Failed to remove Task', error);
        throw error
    }
}

async function choicePreRegistration(boardId, columnId, accountId, payload) {
    try {
        const data = await httpService.patch(`${BASE_URL}/account/${accountId}/boards/${boardId}/column/${columnId}/pre-registration`, payload);
        return data
    } catch (error) {
        console.error('Failed update pre registration', error);
        throw error
    }
}

async function remove(boardId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/boards/${boardId}`);
        return { boardId, ...data };
    } catch (error) {
        console.error('Failed to remove board:', error);
        throw error;
    }
}

function normalizeBoardGroupsToIds(board) {
    if (!board || !Array.isArray(board.groups)) return board;

    const groupsIds = board.groups
        .map((g) => (typeof g === "string" ? g : g?._id))
        .filter(Boolean);

    return { ...board, groups: groupsIds };
}

async function save(board, accountId) {
    try {
        const boardToSave = normalizeBoardGroupsToIds(board);

        if (!boardToSave?._id) {
            throw new Error("save() não cria board. Use addBoard() para criação.");
        }

        const data = await httpService.patch(
            `${BASE_URL}/account/${accountId}/boards/${boardToSave._id}`,
            boardToSave
        );

        const updatedBoard = data?.board || data;

        return { board: normalizeBoardGroupsToIds(updatedBoard) };
    } catch (error) {
        console.error("Failed to save board:", error);

        const message =
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao salvar board.";

        const nextError = new Error(message);
        nextError.originalError = error;
        nextError.response = error?.response;
        nextError.status = error?.response?.status || null;
        nextError.cycle = error?.response?.data?.cycle || null;
        nextError.componentId = error?.response?.data?.componentId || null;

        throw nextError;
    }
}


// TODO: backend nao tem rota POST /:boardId/activities (validar e ajustar quando formos tratar activities)
async function addActivity(boardId, activity) {
    try {
        const data = await httpService.post(`${BASE_URL}/${boardId}/activities`, activity);
        return data; // Retorna a lista atualizada de atividades
    } catch (error) {
        console.error("Erro ao adicionar atividade:", error);
        throw error;
    }
}

async function updateTask(boardId, groupId, task) {
    try {
        const data = await httpService.patch(`${BASE_URL}/boards/${boardId}/groups/${groupId}/tasks/${task._id}`, task);
        return data;
    } catch (error) {
        console.error('Failed to update task:', error);
        throw error;
    }
}
export async function deactivateTasks(taskIds, boardId, groupId, user, accountId) {
    try {
        const data = await httpService.patch(`${BASE_URL}/tasks/deactivate`, {
            taskIds,
            boardId,
            groupId,
            user,
            accountId
        });
        return data;
    } catch (error) {
        console.error("Erro no deactivateTasks service:", error);
        throw error;
    }
}

async function updateGroup(boardId, group) {
    try {
        const taskIds = Array.isArray(group.tasks)
            ? group.tasks.map(t => (typeof t === 'string' ? t : t._id || t.id))
            : [];
        const data = await httpService.patch(`${BASE_URL}/boards/${boardId}/groups/${group._id}`, {
            title: group.title,
            color: group.color,
            tasks: taskIds,
        });
        return data;
    } catch (error) {
        console.error('Failed to update group:', error);
        throw error;
    }
}


async function addComment(boardId, groupId, taskId, comment) {
    try {
        const data = await httpService.post(`${BASE_URL}/boards/${boardId}/groups/${groupId}/tasks/${taskId}/comments`, comment);
        return data;
    } catch (error) {
        console.error('Failed to add a comment:', error);
        throw error;
    }
}

async function deleteComment(boardId, groupId, taskId, commentId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/boards/${boardId}/groups/${groupId}/tasks/${taskId}/comments/${commentId}`);
        return data;
    } catch (error) {
        console.error('Failed to add a comment:', error);
        throw error;
    }
}

async function updateComment(boardId, groupId, taskId, comment) {
    try {
        const data = await httpService.patch(`${BASE_URL}/boards/${boardId}/groups/${groupId}/tasks/${taskId}/comments/${comment._id}`, comment);
        return data;
    } catch (error) {
        console.error('Failed to add a comment:', error);
        throw error;
    }
}

async function updateLabel(boardId, label) {
    try {
        const data = await httpService.patch(`${BASE_URL}/boards/${boardId}/labels`, label);
        return data;
    } catch (error) {
        console.error('Failed to update the label:', error);
        throw error;
    }
}


async function updateOrder(boardId, cmp, accountId) {
    try {
        const data = await httpService.patch(`${BASE_URL}/account/${accountId}/boards/${boardId}/cmpsOrder/${cmp.id}`, cmp);

        return data;
    } catch (error) {
        console.error('Failed to update the label:', error);

        const message =
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao atualizar coluna.";

        const nextError = new Error(message);
        nextError.originalError = error;
        nextError.response = error?.response;
        nextError.status = error?.response?.status || null;
        nextError.cycle = error?.response?.data?.cycle || null;
        nextError.componentId = error?.response?.data?.componentId || null;

        throw nextError;
    }
}

async function deleteOrder(boardId, cmp, accountId) {
    try {
        const data = await httpService.delete(`${BASE_URL}/account/${accountId}/boards/${boardId}/cmpsOrder/${cmp.id}`);
        return data;
    } catch (error) {
        console.error('Failed to update the label:', error);
        throw error;
    }
}

async function getFormPage(pageName) {
    try {
        const [prefix, slug] = String(pageName || "").split("/");
        if (!prefix || !slug) throw new Error("pageName inválido. Esperado: 'prefix/slug'");

        const data = await httpService.get(`${BASE_URL}/forms/${prefix}/${slug}`);
        return data;
    } catch (error) {
        console.error("Erro ao buscar formulário:", error);
        throw error;
    }
}

function getDefaultFilterBoards() {
    return {
        title: '',
        isStarred: false
    }
}

function getDefaultFilterBoard() {
    return {
        title: '',
        memberId: '',
        date: ''
    }
}

function getFilterFromSearchParams(searchParams) {
    const emptyFilter = getDefaultFilterBoard()
    const filterBy = {}
    for (const field in emptyFilter) {
        filterBy[field] = searchParams.get(field) || ''
    }
    return filterBy
}

function getEmptyGroup() {
    return {
        "title": 'New Group',
        "archivedAt": Date.now(),
        "tasks": [],
        "color": '#ffcb00',
    }
}

function getEmptyTask() {
    const timestamp = Date.now();

    return {
        "title": "",
        "updatedBy": {
            "imgUrl": ""
        },
        "comments": [],
        "memberIds": [],
        "timeCreation": timestamp,
        "timeUpdate": timestamp,
        "dynamicFields": [],
    }
}

function getEmptyComment() {
    return {
        "byMember": {
            "_id": null,
            "name": "Guest",
            "email": "",
            "imgUrl": "https://res.cloudinary.com/du63kkxhl/image/upload/v1675013009/guest_f8d60j.png"
        },
        "txt": ""
    }
}

function getEmptyActivity() {
    return {
        "action": "status",
        "createdAt": Date.now(),
        "byMember": {
            "_id": null,
            "name": "Guest",
            "imgUrl": "https://res.cloudinary.com/du63kkxhl/image/upload/v1675013009/guest_f8d60j.png"
        },
        "task": {
            "id": "c101",
            "title": "Replace Logo"
        },
        "from": {},
        "to": {}
    }
}

function getEmptyBoard() {
    return {
        'title': "New Board",
        'description': "New Board",
        'isStarred': false,
        'cmpsOrder': [],
        'cmpsOption': [
            "status-picker",
            "member-picker",
            "date-picker",
            "number-picker",
            "rating-picker",
            "checkbox-picker",
            "link-picker",
            "auto-number-picker",
            "progress-picker",
            "long-text-picker",
            "phone-picker",
            "element-id-picker",
            "color-picker",
            "updated-picker",
            "file-picker",
            "email-picker",
            "time-picker",
            "text-picker"
        ],
        'isBoardModalOpen': false,
        'groups': [],
        'activities': [],
        'taskWidth': "336"
    };
}
